import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import * as Sentry from "@sentry/react";
import { queryClient } from "@/lib/query-client";
import { initPosthog } from "@/lib/posthog";
import { I18nProvider } from "@/i18n/provider";
import App from "@/App";
import { AppErrorFallback } from "@/components/app-error-fallback";
import "@/app.css";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_SHA || undefined,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

initPosthog();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        {/* Inside QueryClientProvider: the locale is resolved from the user's preferences and
            their organisation's default, both of which are queries (POS2-1133). */}
        <I18nProvider>
          <TooltipProvider>
            <App />
            <Toaster position="bottom-right" richColors />
          </TooltipProvider>
        </I18nProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
